import { Suspense } from 'react'
import { Navigate, Route, Routes, useParams, useSearchParams } from 'react-router-dom'

import { Spinner } from '../components/atoms'
import { AssistantProvider } from '../components/organisms/Assistant/AssistantProvider'
import { AppLayout } from '../components/templates/AppLayout'
import { AuthGuard, LicenseGuard, RoleGuard } from './guards'
import { lazyWithRetry } from './lazyWithRetry'
import { planningPublicRoutes, planningRoutes } from './planning-routes'
import { ROUTES } from './types'

// Lazy load pages for code splitting. `lazyWithRetry` auto-reloads once on a
// stale-chunk failure (tab left open across a deploy) instead of blanking.
const LoginPage = lazyWithRetry(() => import('../pages/auth/Login'))
const ForgotPasswordPage = lazyWithRetry(() => import('../pages/auth/ForgotPassword'))
const PasswordResetSentPage = lazyWithRetry(() => import('../pages/auth/PasswordResetSent'))
const SignupPage = lazyWithRetry(() => import('../pages/signup/SignupPage'))
const SignupSuccessPage = lazyWithRetry(() => import('../pages/signup/SignupSuccessPage'))
const CheckoutPage = lazyWithRetry(() => import('../pages/checkout/CheckoutPage'))
const HomePage = lazyWithRetry(() => import('../pages/home/Home'))
const MapPage = lazyWithRetry(() => import('../pages/map/Map'))
const LibraryPage = lazyWithRetry(() => import('../pages/library/Library'))
const ItemDetailPage = lazyWithRetry(() => import('../pages/library/ItemDetail'))
const DashboardListPage = lazyWithRetry(() => import('../pages/dashboard/DashboardList'))
const FormsListPage = lazyWithRetry(() => import('../pages/forms/FormsList'))
const FormsBuilderPage = lazyWithRetry(() => import('../pages/forms/FormsBuilder'))
const FormSubmissionsPage = lazyWithRetry(() => import('../pages/forms/FormSubmissions'))
const FormFillPage = lazyWithRetry(() => import('../pages/forms/FormFill'))
const DashboardBuilderPage = lazyWithRetry(() => import('../pages/dashboard/DashboardBuilder'))
const DashboardViewPage = lazyWithRetry(() => import('../pages/dashboard/DashboardView'))
const GeocodingPage = lazyWithRetry(() => import('../pages/geocoding/Geocoding'))
const GeocodingSessionsPage = lazyWithRetry(() => import('../pages/geocoding/Sessions'))
const GeocodingSessionResultsPage = lazyWithRetry(() => import('../pages/geocoding/SessionResults'))
const SettingsPage = lazyWithRetry(() => import('../pages/settings/Settings'))
const SecurityPage = lazyWithRetry(() => import('../pages/security/Security'))
const ProfilePage = lazyWithRetry(() => import('../pages/profile/Profile'))
const AccountSuspendedPage = lazyWithRetry(() => import('../pages/account-status/AccountSuspendedPage'))
const AccountDeletedPage = lazyWithRetry(() => import('../pages/account-status/AccountDeletedPage'))
const AccountNotFoundPage = lazyWithRetry(() => import('../pages/account-status/AccountNotFoundPage'))
const NotFoundPage = lazyWithRetry(() => import('../pages/error/NotFound'))
const ScriptsListPage = lazyWithRetry(() => import('../pages/scripts/ScriptsListPage'))
const ScriptPage = lazyWithRetry(() => import('../pages/scripts/ScriptPage'))
const CapacityRunPage = lazyWithRetry(() => import('../pages/capacity/CapacityRunPage'))
const CapacityRunsPage = lazyWithRetry(() => import('../pages/capacity/CapacityRunsPage'))
const MonitoringLayout = lazyWithRetry(() => import('../pages/monitoring/MonitoringLayout'))
const MonitoringLibraryPage = lazyWithRetry(() => import('../pages/monitoring/MonitoringLibraryPage'))
const MonitoringSitePage = lazyWithRetry(() => import('../pages/monitoring/MonitoringSitePage'))
// Throwaway realtime-channel verification surface — flagged, not in nav.
const RobotChannelDebugPage = lazyWithRetry(() => import('../pages/debug/RobotChannelDebug'))

function LoadingFallback() {
  return (
    <div className="flex h-screen w-full items-center justify-center">
      <Spinner size="lg" label="Loading..." />
    </div>
  )
}

/** Back-compat: an old `/notebooks/:id` deep link maps to the same script,
 *  preserving the id (a bare <Navigate to="/scripts"> would drop it). */
function NotebookIdRedirect() {
  const { id } = useParams<{ id: string }>()

  return <Navigate to={`${ROUTES.SCRIPTS}/${id}`} replace />
}

/** Renders the robot-channel debug page only when `?robotdebug=1` is present;
 *  otherwise 404s so the route stays hidden from casual navigation. */
function RobotChannelDebugFlagGate() {
  const [params] = useSearchParams()

  if (params.get('robotdebug') !== '1') return <NotFoundPage />

  return <RobotChannelDebugPage />
}

export function AppRoutes() {
  return (
    // AssistantProvider is mounted here — inside <BrowserRouter> (from main.tsx),
    // above <Routes> AND above the <Suspense> boundary — so its chatbotMachine
    // interpretation and session survive both client-side navigation and a
    // lazy-chunk suspend (a Suspense boundary INSIDE this provider replaces only
    // the routed page with the fallback; the provider itself never unmounts).
    // bridge: null / elementId: undefined inside — see AssistantProvider.tsx.
    <AssistantProvider>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Public routes */}
          <Route path={ROUTES.LOGIN} element={<LoginPage />} />
          {/* Legacy generic Cognito self-registration removed — SaaS signup is the tenant
            funnel at /signup. Redirect any stale /auth/register (+ its old verify-email
            step) to the funnel rather than 404. */}
          <Route path={ROUTES.REGISTER} element={<Navigate to={ROUTES.SIGNUP} replace />} />
          <Route path={ROUTES.VERIFY_EMAIL} element={<Navigate to={ROUTES.SIGNUP} replace />} />
          <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
          <Route path={ROUTES.PASSWORD_RESET_SENT} element={<PasswordResetSentPage />} />
          <Route path={ROUTES.SIGNUP} element={<SignupPage />} />
          <Route path={ROUTES.SIGNUP_SUCCESS} element={<SignupSuccessPage />} />
          <Route path={ROUTES.CHECKOUT} element={<CheckoutPage />} />

          {/* Dashboard view — public/anon access (handles own AppLayout when authed) */}
          <Route path={ROUTES.DASHBOARD_VIEW} element={<DashboardViewPage />} />

          {/* Public form fill — no auth required, works anonymously on-prem too */}
          <Route path={ROUTES.FORMS_VIEW} element={<FormFillPage />} />
          {/* Planning applicant self-registration — public, see planningPublicRoutes() */}
          {planningPublicRoutes()}

          {/* Protected routes wrapped in AppLayout */}
          <Route
            element={
              <AuthGuard>
                <AppLayout />
              </AuthGuard>
            }
          >
            <Route path={ROUTES.HOME} element={<HomePage />} />

            <Route path={ROUTES.MAP} element={<MapPage />} />
            <Route path={ROUTES.MAP_VIEW} element={<MapPage />} />

            <Route path={ROUTES.LIBRARY} element={<LibraryPage />} />
            <Route path={ROUTES.LIBRARY_ITEM} element={<ItemDetailPage />} />

            <Route path={ROUTES.PROFILE} element={<ProfilePage />} />

            {/* Dashboard routes */}
            <Route path={ROUTES.DASHBOARD} element={<DashboardListPage />} />
            <Route path={ROUTES.DASHBOARD_BUILDER} element={<DashboardBuilderPage />} />

            {/* Forms — requires license */}
            <Route
              path={ROUTES.FORMS}
              element={
                <LicenseGuard requiredLicenses={['forms']}>
                  <FormsListPage />
                </LicenseGuard>
              }
            />
            {/* Forms builder — requires license */}
            <Route
              path={ROUTES.FORMS_BUILDER}
              element={
                <LicenseGuard requiredLicenses={['forms']}>
                  <FormsBuilderPage />
                </LicenseGuard>
              }
            />
            {/* Forms submissions viewer — requires license */}
            <Route
              path={ROUTES.FORMS_SUBMISSIONS}
              element={
                <LicenseGuard requiredLicenses={['forms']}>
                  <FormSubmissionsPage />
                </LicenseGuard>
              }
            />

            {/* Scripts — one module; a script is a Jupyter notebook. License-gated, on-prem-capable. */}
            <Route
              path={ROUTES.SCRIPTS}
              element={
                <LicenseGuard requiredLicenses={['scripts']}>
                  <ScriptsListPage />
                </LicenseGuard>
              }
            />
            <Route
              path={ROUTES.SCRIPT}
              element={
                <LicenseGuard requiredLicenses={['scripts']}>
                  <ScriptPage />
                </LicenseGuard>
              }
            />
            {/* Back-compat: old /notebooks links redirect to /scripts */}
            <Route path="/notebooks" element={<Navigate to={ROUTES.SCRIPTS} replace />} />
            <Route path="/notebooks/:id" element={<NotebookIdRedirect />} />

            {/* Geocoding — module-gated. The previous comment here said 'geocoding' is
              NOT a real entitlement module because ModuleLabel had no such value, so the
              guard redirected everyone to HOME (#857). That premise no longer holds:
              ModuleLabel.GEOCODING exists, the license server has always issued
              `geocoding`, and the backend now enforces it with requireModule — so an
              unlicensed install got a fully navigable Geocoding UI whose every request
              403'd. Gate it for real. */}
            <Route
              path={ROUTES.GEOCODING_SESSION}
              element={
                <LicenseGuard requiredLicenses={['geocoding']}>
                  <GeocodingSessionResultsPage />
                </LicenseGuard>
              }
            />
            <Route
              path={ROUTES.GEOCODING_SESSIONS}
              element={
                <LicenseGuard requiredLicenses={['geocoding']}>
                  <GeocodingSessionsPage />
                </LicenseGuard>
              }
            />
            <Route
              path={ROUTES.GEOCODING}
              element={
                <LicenseGuard requiredLicenses={['geocoding']}>
                  <GeocodingPage />
                </LicenseGuard>
              }
            />

            {/* Settings - requires admin role */}
            <Route
              path={`${ROUTES.SETTINGS}/*`}
              element={
                <RoleGuard requiredRoles={['admin']}>
                  <SettingsPage />
                </RoleGuard>
              }
            />

            {/* Security - requires license */}
            <Route
              path={`${ROUTES.SECURITY}/*`}
              element={
                <LicenseGuard requiredLicenses={['security']}>
                  <SecurityPage />
                </LicenseGuard>
              }
            />

            {/* Monitoring — no license/module guard. 'monitoring' is not a real entitlement
              module (absent from CLOUD_SUBSCRIPTION_FEATURE_MODULES), which is exactly why
              the backend's read-auth.ts deliberately omits requireModule. A LicenseGuard on a
              non-existent module would redirect every user to '/' — the /geocoding bug (#857)
              repeated. Auth only; the site page hides identity-gated panels itself via
              canViewIdentities from GET /monitoring/sites.

              The layout route owns the roster fetch so navigating library → site costs no
              extra request — see MonitoringLayout.tsx. */}
            {/* The child path is the ABSOLUTE `ROUTES.MONITORING_SITE`, not a bare
              ":siteId". React Router permits an absolute child path when it
              extends the parent's, and using the constant is what keeps
              `ROUTES.MONITORING_SITE` a consumed definition rather than a
              second, silently-drifting copy of this route's shape. Pinned by
              MonitoringLayout.spec.tsx, which builds its route tree from the
              same two constants. */}
            {/* Capacity — same gating rationale as Monitoring below: auth only, no
              LicenseGuard, because 'capacity' is not an entitlement module and a guard on a
              non-existent module redirects every user to '/' (the #857 bug). The backend
              additionally gates on the CALLER being a central operator, so a tenant admin
              gets a 403 from the API rather than a blank page here. */}
            <Route path={ROUTES.CAPACITY} element={<CapacityRunsPage />} />
            <Route path={ROUTES.CAPACITY_RUN} element={<CapacityRunPage />} />

            {/* Monitoring IS an entitlement module (ModuleLabel.MONITORING, issued by
              the license server, enforced by requireModule) — unlike `capacity` above,
              which genuinely is not. It was ungated here for the same reason geocoding
              was, and with the same consequence on an unlicensed install. */}
            <Route
              path={ROUTES.MONITORING}
              element={
                <LicenseGuard requiredLicenses={['monitoring']}>
                  <MonitoringLayout />
                </LicenseGuard>
              }
            >
              <Route index element={<MonitoringLibraryPage />} />
              <Route path={ROUTES.MONITORING_SITE} element={<MonitoringSitePage />} />
            </Route>

            {/* Planning — LicenseGuard ['planning'], no RoleGuard; see planning-routes.tsx. */}
            {planningRoutes()}

            {/* Robot realtime channel debug — hidden, flag-gated (?robotdebug=1).
              Throwaway live-verify surface; the real fleet dashboard is sub-project 3. */}
            <Route path={ROUTES.ROBOT_CHANNEL_DEBUG} element={<RobotChannelDebugFlagGate />} />
          </Route>

          {/* Redirects from old /admin/* paths — must be outside AuthGuard so bookmarks survive */}
          <Route path={ROUTES.ADMIN_TENANTS} element={<Navigate to={ROUTES.SETTINGS_TENANTS} replace />} />
          <Route path={ROUTES.ADMIN_AUDIT_LOGS} element={<Navigate to={ROUTES.SETTINGS_AUDIT_LOGS} replace />} />

          {/* Account status pages — outside AuthGuard (user is authed via Cognito but tenant is dead) */}
          <Route path={ROUTES.ACCOUNT_SUSPENDED} element={<AccountSuspendedPage />} />
          <Route path={ROUTES.ACCOUNT_DELETED} element={<AccountDeletedPage />} />
          <Route path={ROUTES.ACCOUNT_NOT_FOUND} element={<AccountNotFoundPage />} />

          {/* 404 Not Found */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </AssistantProvider>
  )
}

export { ROUTES } from './types'
export type { RouteConfig, RoutePath } from './types'
